import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import InquiryModal from "@/components/InquiryModal";
import { sanitizeRichHtml } from "@/lib/blogHtml";
import { useSEO } from "@/lib/seo";

export default function BlogPost() {
  const { slug } = useParams();
  const embedded = (window.INITIAL_DATA?.type === "blog_post" && window.INITIAL_DATA.payload?.slug === slug)
    ? window.INITIAL_DATA.payload : null;
  const [post, setPost] = useState(embedded || null);
  const [author, setAuthor] = useState(null);
  const [inquiryOpen, setInquiryOpen] = useState(false);

  useEffect(() => {
    api.get(`/posts/${slug}`).then((r) => setPost(r.data)).catch(() => setPost(false));
  }, [slug]);

  useEffect(() => {
    if (post?.author_slug) {
      api.get(`/blog/authors/${post.author_slug}`).then((r) => setAuthor(r.data.author)).catch(() => {});
    }
  }, [post?.author_slug]);

  const pseo = post?.seo || {};
  useSEO({
    title: post ? (pseo.meta_title || `${post.title} | Training Basket`) : "Guide | Training Basket",
    description: pseo.meta_description || post?.excerpt || "A practical guide from the Training Basket team.",
    canonical: pseo.canonical_url || `/blog/${slug}`,
    image: pseo.og_image || post?.image,
    ogTitle: pseo.og_title,
    ogDescription: pseo.og_description,
    keywords: pseo.keywords,
    noindex: !!pseo.noindex,
    nofollow: !!pseo.nofollow,
  });

  if (post === false) return <div className="max-w-3xl mx-auto px-6 py-24 text-center"><p className="font-bold text-xl">Article not found</p></div>;
  if (!post) return <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 min-h-[100vh]"><div className="h-96 rounded-3xl bg-slate-100 animate-pulse" /></div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="blog-post-page">
      <Link to="/blog" className="text-sm font-semibold text-slate-500 hover:text-rose-600 flex items-center gap-1.5" data-testid="back-to-blog"><ArrowLeft className="w-4 h-4" /> All guides</Link>
      <span className="inline-flex mt-6 px-3 py-1 rounded-full text-xs font-semibold bg-rose-100 text-rose-800 border border-rose-200/50">{post.category}</span>
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-4 leading-tight">{post.title}</h1>
      {post.author_slug ? (
        <Link to={`/blog/author/${post.author_slug}`} className="text-sm text-slate-400 mt-3 hover:text-amber-700 transition-colors" data-testid="post-author-link">By <span className="font-semibold underline decoration-amber-300 underline-offset-2">{post.author}</span></Link>
      ) : (
        <p className="text-sm text-slate-400 mt-3">By {post.author}</p>
      )}
      <img src={post.image} alt={post.title} className="w-full h-72 sm:h-96 object-cover rounded-3xl mt-8" />
      {post.content_html ? (
        <div className="blog-content mt-10" data-testid="blog-post-content"
          dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(post.content_html) }} />
      ) : (
        <div className="mt-10 flex flex-col gap-6" data-testid="blog-post-content">
          {post.content.split("\n\n").map((para, i) => (
            <p key={i} className="text-slate-600 leading-relaxed text-base sm:text-lg">{para}</p>
          ))}
        </div>
      )}
      {author && (
        <div className="mt-12 rounded-3xl bg-slate-50 border border-slate-200 p-6 sm:p-8 flex flex-col sm:flex-row gap-6 sm:items-center" data-testid="author-card">
          {author.photo && <img src={author.photo} alt={author.name} className="w-20 h-20 rounded-2xl object-cover border-4 border-white shadow shrink-0" data-testid="author-card-photo" />}
          <div className="flex-1">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-amber-600">Written by</p>
            <Link to={`/blog/author/${author.slug}`} className="text-xl font-extrabold text-slate-900 hover:text-amber-700 underline decoration-amber-300 underline-offset-4 transition-colors" data-testid="author-card-name">{author.name}</Link>
            <p className="text-sm text-slate-500 mt-2 leading-relaxed" data-testid="author-card-bio">{author.bio}</p>
          </div>
          <Link to={`/blog/author/${author.slug}`} className="rounded-xl bg-[#17263B] hover:bg-[#0f1c2e] text-white text-sm font-bold px-6 py-3 transition-colors shrink-0" data-testid="author-card-profile">Profile</Link>
        </div>
      )}
      <div className="mt-14 rounded-3xl bg-gradient-to-r from-rose-600 to-amber-600 p-8 sm:p-10 text-white text-center">
        <h3 className="text-xl sm:text-2xl font-bold">Ready to shortlist programs?</h3>
        <p className="text-rose-100 text-sm mt-2">Get a counselor-matched list based on your profile and budget.</p>
        <Button className="mt-6 bg-white text-rose-700 hover:bg-rose-50 font-bold rounded-xl" onClick={() => setInquiryOpen(true)} data-testid="blog-cta-button">Get my shortlist</Button>
      </div>
      <InquiryModal open={inquiryOpen} onOpenChange={setInquiryOpen} />
    </div>
  );
}
